import { type AttributeValue, type Attributes, type Meter, metrics } from '@opentelemetry/api';
import { type LogAttributes, type Logger, SeverityNumber, logs } from '@opentelemetry/api-logs';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { LoggerProvider } from '@opentelemetry/sdk-logs';
import { MeterProvider } from '@opentelemetry/sdk-metrics';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import type { LogLevel, TelemetryConfig } from '../types';

// Instrumentation-scope identity for the library itself. This is distinct from
// the *service* resource (service.name / service.version), which the consuming
// application supplies via TelemetryConfig.
const METER_NAME = '@seeqdev/telemetry';
const METER_VERSION = '0.0.1';

// Maps the webserver-style log levels to OTel severity numbers. severityText is
// derived from the level itself (uppercased) at emit time.
const LEVEL_TO_SEVERITY: Record<LogLevel, SeverityNumber> = {
  trace: SeverityNumber.TRACE,
  debug: SeverityNumber.DEBUG,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR,
  fatal: SeverityNumber.FATAL,
};

// The SDK is a process-wide singleton held in module scope. We model it with
// free functions + module state (the `@seeqdev/api` axios.utilities.ts
// convention) rather than a class that polices its own single-instance
// lifecycle.
let meterProvider: MeterProvider | null = null;
let meter: Meter | null = null;
let loggerProvider: LoggerProvider | null = null;
let logger: Logger | null = null;
let commonAttributes: Attributes = {};

/**
 * Initialize the telemetry SDK from the supplied config: builds the OTel
 * MeterProvider (resource = service.name / service.version / custom attributes)
 * and registers it as the global provider. Idempotent — a second call with the
 * SDK already initialized is a no-op and keeps the existing provider. Callers
 * that want re-init to be an error should go through {@link setupTelemetry}.
 */
export function initTelemetrySdk(config: TelemetryConfig): void {
  if (meterProvider) {
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    ...config.resourceAttributes,
  });

  meterProvider = new MeterProvider({ resource, readers: config.readers });
  metrics.setGlobalMeterProvider(meterProvider);
  meter = metrics.getMeter(METER_NAME, METER_VERSION);

  // Logs are opt-in: only stand up a LoggerProvider when the consumer supplies
  // log record processors (exporters), mirroring the metric-readers contract.
  if (config.logRecordProcessors && config.logRecordProcessors.length > 0) {
    loggerProvider = new LoggerProvider({ resource, processors: config.logRecordProcessors });
    logs.setGlobalLoggerProvider(loggerProvider);
    logger = logs.getLogger(METER_NAME, METER_VERSION);
  }
}

/** Whether {@link initTelemetrySdk} has run and the provider is live. */
export function isTelemetrySdkInitialized(): boolean {
  return meterProvider !== null;
}

/** The library Meter, or `null` before initialization. */
export function getMeter(): Meter | null {
  return meter;
}

/** The library Logger, or `null` before init / when no log processors were configured. */
export function getLogger(): Logger | null {
  return logger;
}

/**
 * Emit an OTel log record at the given level. No-op before init or when no log
 * record processors were configured. `severityText` is the uppercased level.
 */
export function emitLog(level: LogLevel, body: string, attributes?: LogAttributes): void {
  logger?.emit({
    severityNumber: LEVEL_TO_SEVERITY[level],
    severityText: level.toUpperCase(),
    body,
    attributes,
  });
}

/**
 * Set an attribute applied to every metric recording that reads
 * {@link getCommonAttributes} (e.g. the current route). Read lazily at record
 * time so instruments always see the latest value.
 */
export function setCommonAttribute(key: string, value: AttributeValue): void {
  commonAttributes = { ...commonAttributes, [key]: value };
}

export function getCommonAttributes(): Attributes {
  return commonAttributes;
}

/** Flush all readers and log processors immediately (e.g. on page hide). No-op before init. */
export async function forceFlush(): Promise<void> {
  await Promise.all([meterProvider?.forceFlush(), loggerProvider?.forceFlush()]);
}

/** Shut down the providers and clear all module state. */
export async function shutdown(): Promise<void> {
  await Promise.all([meterProvider?.shutdown(), loggerProvider?.shutdown()]);
  meterProvider = null;
  meter = null;
  loggerProvider = null;
  logger = null;
  commonAttributes = {};
}

/**
 * Visible for testing only. Synchronously clears module state without shutting
 * the providers down (tests that need a clean teardown should `await shutdown()`
 * first).
 */
export function resetTelemetrySdkForTesting(): void {
  meterProvider = null;
  meter = null;
  loggerProvider = null;
  logger = null;
  commonAttributes = {};
}
